import type { DriverShipment } from "@khazzin/api-client";
import { formatMoney } from "@khazzin/i18n/money";
import { Icon } from "@khazzin/ui/icons/Icon";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { Bdi } from "@khazzin/ui/primitives/Bdi";
import { ConfirmDialog } from "@khazzin/ui/primitives/Dialog";
import { type LucideIcon, MapPin, MessageCircle, Navigation, Phone } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { useI18n } from "../../i18n";
import { CodConfirm } from "../../modules/cod/CodConfirm";
import { OutcomeActions } from "../../modules/outcome/OutcomeActions";
import { type Day, useCommand } from "../../offline/Offline";
import { PickupOutcome } from "./PickupOutcome";
import { callHref, isDone, isPickup, navigateHref, whatsappHref } from "./stops";
import { useStops, WindowChip } from "./Today";

/**
 * Stop detail (DA-10-16, EP10-S16): the customer and the order written out
 * (ADJ-1022-8b), where the stop is, the three hand-offs a
 * driver needs at the kerb — Call, WhatsApp, Navigate — its window, what is
 * delivered and collected there, and "Pin wrong?", which sets the pin to where
 * the driver stands. The pin is a queued command like any other, so it works
 * with no signal and syncs as `driver_confirmed`.
 *
 * Maps opens by deep link; an embedded map arrives with route optimisation
 * (EP99). The outcome actions are EP10-S17's, under the hand-offs, with the
 * cash at the door (EP10-S19) under them; a pickup's
 * (Collected, Not collected) are EP03-S21's, in place of the shipment block.
 */
export function StopDetail() {
	const { shipmentId = "" } = useParams();
	const { t } = useI18n();
	const { stops } = useStops();
	const setPin = useCommand("set_pin");
	const [confirming, setConfirming] = useState(false);
	const [pinError, setPinError] = useState(false);

	const index = stops.findIndex((s) => s.shipmentId === shipmentId);
	const stop = stops[index];
	if (stop === undefined) {
		// Not on this driver's day (reassigned away, or never theirs).
		return <Navigate to="/today" replace />;
	}

	async function confirmPin() {
		setPinError(false);
		let at: GeolocationPosition;
		try {
			at = await new Promise<GeolocationPosition>((resolve, reject) =>
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					enableHighAccuracy: true,
					timeout: 20_000,
				}),
			);
		} catch (err) {
			setPinError(true);
			throw err;
		}
		const pin = { lat: at.coords.latitude, lng: at.coords.longitude };
		await setPin(
			{ mode: "driver_confirmed", ...pin },
			{
				shipmentId,
				apply: (d: Day) => {
					const s = d.shipments[shipmentId];
					return s === undefined
						? d
						: {
								...d,
								shipments: {
									...d.shipments,
									[shipmentId]: { ...s, pin, pinSource: "driver_confirmed" },
								},
							};
				},
			},
		);
	}

	const greeting = t(isPickup(stop) ? "driver.pickup.greeting" : "driver.stop.greeting", {
		name: stop.recipientName,
	});
	const pinState = stop.pin
		? `driver.stop.pin.${stop.pinSource ?? "geocoded"}`
		: "driver.stop.pin.none";

	return (
		<div className="flex flex-col gap-4">
			<Link to="/today" className="min-h-12 content-center text-[14px] font-bold text-brand-ink">
				{t("driver.stop.back")}
			</Link>

			<header className="flex items-start gap-3">
				<span className="grid size-9 shrink-0 place-items-center rounded-full bg-bg-subtle text-[14px] font-bold text-fg">
					{index + 1}
				</span>
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<h1 className="text-[18px] font-bold text-fg">{stop.recipientName}</h1>
					<div className="flex flex-wrap gap-1.5">
						<StatusChip tone={isDone(stop.status) ? "success" : "neutral"} size="sm">
							{t(`driver.status.${stop.status}`)}
						</StatusChip>
						<WindowChip window={stop.window} done={isDone(stop.status)} />
					</div>
				</div>
			</header>

			<CustomerSection stop={stop}>
				<p className="flex items-center gap-1.5 text-[13px] text-fg-muted">
					<Icon as={MapPin} size={14} />
					{t(pinState)}
				</p>
				<button
					type="button"
					className="min-h-12 self-start text-[14px] font-bold text-brand-ink underline"
					onClick={() => setConfirming(true)}
				>
					{t("driver.stop.pinWrong")}
				</button>
			</CustomerSection>

			<div className="grid grid-cols-3 gap-2">
				<HandOff href={callHref(stop.recipientPhone)} icon={Phone} label={t("driver.stop.call")} />
				<HandOff
					href={whatsappHref(stop.recipientPhone, greeting)}
					icon={MessageCircle}
					label={t("driver.stop.whatsapp")}
				/>
				<HandOff href={navigateHref(stop)} icon={Navigation} label={t("driver.stop.navigate")} />
			</div>

			{isPickup(stop) ? (
				<PickupOutcome stop={stop} />
			) : (
				<>
					<OutcomeActions stop={stop} />
					<CodConfirm stop={stop} />
					<ParcelSection stop={stop} />
				</>
			)}

			<ConfirmDialog
				open={confirming}
				onOpenChange={(open) => {
					setConfirming(open);
					setPinError(false);
				}}
				title={t("driver.stop.pinTitle")}
				description={t("driver.stop.pinBody")}
				confirmLabel={t("driver.stop.pinConfirm")}
				cancelLabel={t("driver.stop.pinCancel")}
				closeLabel={t("driver.stop.pinCancel")}
				onConfirm={confirmPin}
			>
				{pinError && (
					<p role="alert" className="text-[13px] text-danger-fg">
						{t("driver.stop.pinUnavailable")}
					</p>
				)}
			</ConfirmDialog>
		</div>
	);
}

/**
 * Who the stop is for, as text (ADJ-1022-8b): the name, the phone number
 * written out (not only behind Call and WhatsApp) and the full address with
 * its city and province. The live stop adds its pin under it; History's
 * read-only detail shows it as it is.
 */
export function CustomerSection({
	stop,
	children,
}: {
	stop: DriverShipment;
	children?: ReactNode;
}) {
	const { t } = useI18n();
	return (
		<section aria-label={t("driver.stop.customer")} className="flex flex-col gap-1 card p-4">
			<h2 className="text-[15px] font-bold text-fg">{t("driver.stop.customer")}</h2>
			<p className="text-[15px] text-fg">{stop.recipientName}</p>
			{stop.recipientPhone !== "" && (
				<p className="text-[15px] text-fg">
					<Bdi dir="ltr">{stop.recipientPhone}</Bdi>
				</p>
			)}
			<p className="text-[15px] text-fg">{stop.address}</p>
			<p className="text-[13px] text-fg-muted">
				{[stop.cityName, stop.provinceCode].filter(Boolean).join(" · ")}
			</p>
			{children}
		</section>
	);
}

/**
 * What the stop delivers and collects (ADJ-1022-8b): the tracking number,
 * every line of the order — title, SKU, quantity — and its COD or prepaid.
 * History's detail shows it too.
 */
export function ParcelSection({ stop }: { stop: DriverShipment }) {
	const { t, locale } = useI18n();
	// `?? []`: a list once written as null must not take the screen down.
	const lines = stop.lines ?? [];
	return (
		<section aria-label={t("driver.stop.order")} className="flex flex-col gap-2 card p-4">
			<h2 className="text-[15px] font-bold text-fg">{t("driver.stop.order")}</h2>
			{lines.length > 0 && (
				<ul className="flex flex-col divide-y divide-border">
					{lines.map((l) => (
						<li key={l.orderLineId} className="flex items-center gap-3 py-2 text-[14px]">
							<span className="flex min-w-0 flex-1 flex-col">
								<span className="text-fg">{l.title}</span>
								<span className="text-[13px] text-fg-muted">
									<Bdi dir="ltr">{l.sku}</Bdi>
								</span>
							</span>
							<span className="font-bold text-fg">
								{t("driver.stop.quantity", { count: l.quantity })}
							</span>
						</li>
					))}
				</ul>
			)}
			<div className="flex flex-wrap items-center gap-2">
				<span className="me-auto text-[14px] text-fg">{stop.trackingNumber}</span>
				<StatusChip tone="neutral" size="sm">
					{t("driver.today.parcels", { count: 1 })}
				</StatusChip>
				{stop.cod && stop.cod.amountMinor > 0 ? (
					<StatusChip tone="warning" size="sm">
						{t(stop.codPaidToStore === true ? "driver.today.codForCompany" : "driver.today.cod", {
							amount: formatMoney(stop.cod, locale),
						})}
					</StatusChip>
				) : (
					<StatusChip tone="neutral" size="sm">
						{t("driver.stop.prepaid")}
					</StatusChip>
				)}
			</div>
		</section>
	);
}

/** One of the three large buttons: it leaves the app for the phone, WhatsApp or Maps. */
function HandOff({ href, icon, label }: { href: string; icon: LucideIcon; label: string }) {
	return (
		<a
			href={href}
			target={href.startsWith("tel:") ? undefined : "_blank"}
			rel="noreferrer"
			className="flex min-h-14 flex-col items-center justify-center gap-1 rounded-lg bg-brand p-2 text-[14px] font-bold text-fg-on-brand"
		>
			<Icon as={icon} size={20} />
			{label}
		</a>
	);
}
